/**
 * Lectura de lo que devuelve (o lanza) `req()`.
 *
 * `req()` NO lanza con un 422: lo devuelve entero, con `success:false`, `error` y `code` en el
 * nivel raíz y SIN `data`. Cualquier lectura que mire `r.data` antes de chequear `success` ve
 * `undefined` y puede terminar diciendo «salió» sobre algo que el backend rechazó: es el falso
 * verde que ya pasó en Activa (`campana-nueva/utils/resultadoPrueba.ts`). Por eso el guard de
 * `success === false` va SIEMPRE primero, acá y en un solo lugar.
 *
 * Todo lo demás (400, 401 tras el reintento, 404, 409, 413, 5xx) lanza un Error con `status` y
 * `body`; un error de red lanza un TypeError de fetch sin `status`.
 */

export type Lectura<D> =
  | { ok: true; data: D }
  | { ok: false; status: number; code: string; error: string; extra: Record<string, unknown> };

const RESERVADAS = new Set(["status", "success", "error", "code", "data"]);

/** Normaliza lo que devuelve req() (2xx o 422). */
export function leerRespuesta<D>(r: unknown): Lectura<D> {
  const o = (r && typeof r === "object" ? r : null) as Record<string, unknown> | null;
  // Sin objeto no hay nada que leer: se trata como rechazo (fail-closed), nunca como éxito.
  if (!o) return { ok: false, status: 0, code: "SIN_RESPUESTA", error: "", extra: {} };
  const status = typeof o.status === "number" ? o.status : 200;
  if (o.success === false || status >= 400) {
    const extra: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(o)) if (!RESERVADAS.has(k)) extra[k] = v;
    return {
      ok: false,
      status: typeof o.status === "number" ? o.status : 422,
      code: typeof o.code === "string" ? o.code : "",
      error: typeof o.error === "string" ? o.error : "",
      extra,
    };
  }
  return { ok: true, data: o.data as D };
}

/** Normaliza lo que LANZA req() (409, 404, 5xx…) o un error de red (TypeError de fetch sin status). */
export function leerError(e: unknown): { status: number; code: string; error: string; body: Record<string, unknown> } {
  const x = (e && typeof e === "object" ? e : {}) as { status?: unknown; body?: unknown };
  const status = typeof x.status === "number" ? x.status : 0;
  const body = (x.body && typeof x.body === "object" ? x.body : {}) as Record<string, unknown>;
  // Sólo el `error` del cuerpo cuenta como texto del backend. El `message` del Error es
  // «HTTP 401» o «Failed to fetch»: no le dice nada al operador, así que se deja que
  // `textoError` ponga el genérico por status.
  return {
    status,
    code: typeof body.code === "string" ? body.code : status === 0 ? "RED" : "",
    error: typeof body.error === "string" ? body.error : "",
    body,
  };
}

/** Texto para el operador: el `error` del backend; si no hay, un genérico por status. */
export function textoError(x: { status: number; error?: string }): string {
  if (x.error && x.error.trim()) return x.error.trim();
  if (x.status === 0) return "No pudimos conectar con el servidor.";
  if (x.status === 401) return "Se venció la sesión: volvé a elegir el comercio arriba de todo.";
  if (x.status === 403) return "No tenés permiso para ver esto con el comercio elegido.";
  if (x.status === 404) return "No encontramos lo que buscabas.";
  if (x.status === 413) return "Los archivos pesan más de 8 MB.";
  if (x.status === 429) return "Demasiados pedidos seguidos. Esperá un minuto y probá de nuevo.";
  if (x.status >= 500) return "El servidor tuvo un problema. Probá de nuevo en un rato.";
  return `No se pudo completar (error ${x.status}).`;
}

const CODIGOS_CLAVE = new Set(["NO_KEY", "CLAVE_INVALIDA", "CLAVE_DESCONOCIDA", "SECRETO_CORRUPTO", "FORMATO_SECRETO"]);

/** true para los 503 de la clave de cifrado: NO_KEY, CLAVE_INVALIDA, CLAVE_DESCONOCIDA, SECRETO_CORRUPTO, FORMATO_SECRETO. */
export function esErrorDeClave(code: string | undefined): boolean {
  return !!code && CODIGOS_CLAVE.has(code);
}

export const TEXTO_ERROR_CLAVE = "Problema con la clave de cifrado del servidor (WA_OFICIAL_CLAVE). Volvé a cargar el Auth Token o avisale a quien administra el deploy.";

/**
 * Espera la respuesta como mucho `ms`: después rechaza como un corte de red (status 0). `req()` no corta solo, y un
 * pedido que nunca vuelve dejaba la pantalla esperando para siempre. El pedido sigue en el servidor: quien lo usa tiene
 * que averiguar cómo quedó antes de ofrecer otra vez (el confirmar lo hace).
 */
export function conLimite<T>(p: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(Object.assign(new Error("Sin respuesta del servidor"), { status: 0 })), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
  });
}
